/**
 * /versionsbuilt — public build-provenance + environment status page.
 *
 * Purpose: prove that dev / QA / prod run the *same* front-end artifact, and
 * that the artifact the CDN serves is the one this tab is running. The build
 * metadata (version, git commit, build timestamp) is inlined at build time and
 * is byte-identical across environments because the identical `dist/` is
 * deployed to all three Workers — only the runtime config.json differs. Open
 * https://<env>.karexia.com/versionsbuilt on each env and compare the "Build
 * fingerprint": if it matches, the environments are on the same build.
 *
 * Real sources (all public, no auth):
 *   - `__BUILD_META__` / `__APP_VERSION__` — inlined by vite.config.ts;
 *   - `/version.json` — emitted at build time next to the bundle (the marker
 *     useVersionCheck polls). Served ≠ booted ⇒ this tab runs a STALE bundle:
 *     the classic edge-cached index.html after a deploy without a CDN purge.
 *     The page names it and offers the same hard reload as the update banner;
 *   - `/config.json` — per-environment runtime config;
 *   - gateway `GET /health` (api-gateway src/api/health.py: status, version,
 *     timestamp, uptime_seconds, checks.redis) and `GET /health/services`
 *     (every downstream microservice: status, latency, circuit state, error);
 *   - the service-worker registration (public/sw.js) state.
 *
 * Public route (no auth) so the version can be checked even if login is down.
 */
import { type ReactNode, useCallback, useEffect, useState } from 'react';

import { BOOT_VERSION, hardReload } from '@/app/useVersionCheck';
import { useT, tr } from '@/i18n';

interface RuntimeConfig {
  env?: string;
  apiBaseUrl?: string;
  wsUrl?: string;
  keycloak?: { authority?: string; clientId?: string };
}

interface HealthCheck {
  status?: string;
  latency_ms?: number;
  error?: string;
}

interface BackendHealth {
  status?: string;
  version?: string;
  timestamp?: string;
  uptime_seconds?: number;
  checks?: Record<string, HealthCheck>;
}

interface ServiceHealth {
  name: string;
  status: string;
  latency_ms?: number | null;
  error?: string | null;
  circuit_state?: string | null;
}

interface ServicesHealth {
  status: string;
  timestamp: string;
  services: ServiceHealth[];
  healthy_count: number;
  total_count: number;
}

/** Shape of dist/version.json (vite.config.ts VERSION_JSON). */
interface DeployedVersion {
  version?: string;
  buildTime?: string;
  buildEpochMs?: number;
  gitCommit?: string;
  gitCommitFull?: string;
}

interface SwState {
  supported: boolean;
  registered: boolean;
  active: boolean;
  waiting: boolean;
  scope: string | null;
}

const META = __BUILD_META__;

function fmtUTC(iso: string): string {
  if (!iso || iso === 'unknown') return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ` +
    `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())} UTC`
  );
}

function fmtUptime(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / 86_400);
  const h = Math.floor((s % 86_400) / 3_600);
  const m = Math.floor((s % 3_600) / 60);
  if (d > 0) return `${d} j ${h} h ${m} min`;
  if (h > 0) return `${h} h ${m} min`;
  return `${m} min`;
}

function realmOf(authority?: string): string {
  const m = authority?.match(/\/realms\/([^/]+)/);
  return m ? m[1] : '—';
}

async function fetchJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { cache: 'no-store', headers: { 'cache-control': 'no-cache' } });
  if (!r.ok) throw new Error(`${url.replace(/\?.*$/, '')} ${r.status}`);
  return (await r.json()) as T;
}

async function readSwState(): Promise<SwState> {
  if (!('serviceWorker' in navigator)) {
    return { supported: false, registered: false, active: false, waiting: false, scope: null };
  }
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    return {
      supported: true,
      registered: Boolean(reg),
      active: Boolean(reg?.active),
      waiting: Boolean(reg?.waiting),
      scope: reg?.scope ?? null,
    };
  } catch {
    return { supported: true, registered: false, active: false, waiting: false, scope: null };
  }
}

function statusTone(status: string | undefined): string {
  switch ((status ?? '').toLowerCase()) {
    case 'healthy':
    case 'ok':
    case 'up':
      return 'bg-success';
    case 'degraded':
      return 'bg-warning';
    default:
      return 'bg-danger';
  }
}

function Row({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-border py-2 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-sm font-medium text-muted">{label}</span>
      <span
        className={`text-sm text-foreground ${mono ? 'font-mono' : ''} break-all sm:text-right`}
      >
        {value}
      </span>
    </div>
  );
}

function Card({
  title,
  children,
  aside,
}: {
  title: string;
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <section className="rounded-token border border-border bg-surface p-4 shadow-elevation-1">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

function Pill({ tone, children }: { tone: 'success' | 'warning' | 'danger' | 'muted'; children: ReactNode }) {
  const cls =
    tone === 'success'
      ? 'bg-success/15 text-success'
      : tone === 'warning'
        ? 'bg-warning/15 text-warning'
        : tone === 'danger'
          ? 'bg-danger/15 text-danger'
          : 'bg-surface-2 text-muted';
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>
      {children}
    </span>
  );
}

const smallBtn =
  'inline-flex items-center justify-center rounded-token border border-border bg-surface px-3 py-1 text-xs font-medium text-foreground hover:bg-surface-2 disabled:opacity-50';

// `env` is the literal config key name (same on every deployment and locale).
const ENV_KEY = 'env';

export function VersionsBuiltPage() {
  const { t } = useT();
  const [cfg, setCfg] = useState<RuntimeConfig | null>(null);
  const [cfgErr, setCfgErr] = useState<string | null>(null);
  const [health, setHealth] = useState<BackendHealth | null>(null);
  const [healthErr, setHealthErr] = useState<string | null>(null);
  const [services, setServices] = useState<ServicesHealth | null>(null);
  const [servicesErr, setServicesErr] = useState<string | null>(null);
  const [deployed, setDeployed] = useState<DeployedVersion | null>(null);
  const [deployedErr, setDeployedErr] = useState<string | null>(null);
  const [sw, setSw] = useState<SwState | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [checkedAt, setCheckedAt] = useState<string>(() => new Date().toISOString());
  const [copied, setCopied] = useState(false);
  const [reloading, setReloading] = useState(false);

  const refresh = useCallback(async (alive: { current: boolean } = { current: true }) => {
    setRefreshing(true);
    setHealthErr(null);
    setServicesErr(null);
    setDeployedErr(null);

    const configTask = fetchJson<RuntimeConfig>('/config.json')
      .then(async (c) => {
        if (!alive.current) return;
        setCfg(c);
        setCfgErr(null);
        if (!c.apiBaseUrl) {
          setHealthErr('apiBaseUrl absent de config.json');
          setServicesErr('apiBaseUrl absent de config.json');
          return;
        }
        const base = c.apiBaseUrl.replace(/\/$/, '');
        await Promise.all([
          fetchJson<BackendHealth>(`${base}/health`)
            .then((h) => alive.current && setHealth(h))
            .catch((e: Error) => alive.current && setHealthErr(e.message)),
          fetchJson<ServicesHealth>(`${base}/health/services`)
            .then((s) => alive.current && setServices(s))
            .catch((e: Error) => alive.current && setServicesErr(e.message)),
        ]);
      })
      .catch((e: Error) => alive.current && setCfgErr(e.message));

    const versionTask = fetchJson<DeployedVersion>(`/version.json?ts=${Date.now()}`)
      .then((v) => alive.current && setDeployed(v))
      .catch((e: Error) => alive.current && setDeployedErr(e.message));

    const swTask = readSwState().then((s) => alive.current && setSw(s));

    await Promise.all([configTask, versionTask, swTask]);
    if (alive.current) {
      setCheckedAt(new Date().toISOString());
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const alive = { current: true };
    void refresh(alive);
    return () => {
      alive.current = false;
    };
  }, [refresh]);

  const env = cfg?.env ?? '—';
  const fingerprint = `${META.version}+${META.gitCommit}@${META.buildTime}`;

  // Served (version.json) vs booted (__APP_VERSION__) — the stale-bundle check.
  const servedVersion = deployed?.version ?? null;
  const parity: 'same' | 'stale' | 'unknown' =
    servedVersion === null ? 'unknown' : servedVersion === BOOT_VERSION ? 'same' : 'stale';

  const copyFingerprint = async () => {
    try {
      await navigator.clipboard.writeText(fingerprint);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the text stays selectable */
    }
  };

  const redis = health?.checks?.redis;

  return (
    <div className="min-h-screen bg-surface-2 px-4 py-8 text-foreground">
      <div className="mx-auto flex max-w-2xl flex-col gap-4">
        <header className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-2xl font-bold">{t('routes.versions.versionsBuiltPage.versionsBuild')}</h1>
            <p className="mt-1 text-sm text-muted">{t('routes.versions.versionsBuiltPage.pubsocialnetKarexiaBuildProvenanceForDev')}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-token bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
              {ENV_KEY}: {env}
            </span>
            <button
              type="button"
              className={smallBtn}
              onClick={() => void refresh()}
              disabled={refreshing}
            >
              {refreshing ? t('routes.versions.versionsBuiltPage.checking') : t('routes.versions.versionsBuiltPage.reCheck')}
            </button>
          </div>
        </header>

        <Card
          title={t('routes.versions.versionsBuiltPage.buildFingerprintCompareAcrossEnvironment')}
          aside={
            <button type="button" className={smallBtn} onClick={() => void copyFingerprint()}>
              {copied ? t('routes.versions.versionsBuiltPage.copied') : t('routes.versions.versionsBuiltPage.copy')}
            </button>
          }
        >
          <div className="rounded-token bg-surface-2 p-3">
            <code className="block break-all font-mono text-sm text-foreground">{fingerprint}</code>
          </div>
          <p className="mt-2 text-xs text-muted">{t('routes.versions.versionsBuiltPage.identicalOnDevQaAndProd')}{' '}
            <code className="font-mono">env</code>{' '}{t('routes.versions.versionsBuiltPage.labelAndConfigBelowDifferPer')}</p>
        </Card>

        <Card
          title={t('routes.versions.versionsBuiltPage.servedArtifactVsThisPage')}
          aside={
            parity === 'same' ? (
              <Pill tone="success">{t('routes.versions.versionsBuiltPage.upToDate')}</Pill>
            ) : parity === 'stale' ? (
              <Pill tone="warning">{t('routes.versions.versionsBuiltPage.stale')}</Pill>
            ) : (
              <Pill tone="muted">{t('routes.versions.versionsBuiltPage.undetermined')}</Pill>
            )
          }
        >
          <Row label={t('routes.versions.versionsBuiltPage.startedVersionThisBundle')} value={BOOT_VERSION} mono />
          <Row
            label={t('routes.versions.versionsBuiltPage.servedVersionVersionJson')}
            value={
              deployedErr
                ? `non disponible : ${deployedErr}`
                : servedVersion ?? (refreshing ? tr('routes.versions.versionsBuiltPage.checking') : '—')
            }
            mono
          />
          {deployed?.gitCommit && (
            <Row label={t('routes.versions.versionsBuiltPage.servedCommit')} value={deployed.gitCommit} mono />
          )}
          {deployed?.buildTime && (
            <Row label={t('routes.versions.versionsBuiltPage.servedBuild')} value={fmtUTC(deployed.buildTime)} mono />
          )}
          {parity === 'stale' && (
            <div className="mt-3 rounded-token border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-foreground">
              <p>{t('routes.versions.versionsBuiltPage.theCdnServesANewerArtifact')}{' '}<code className="font-mono">index.html</code>{' '}{t('routes.versions.versionsBuiltPage.stillCachedDeploymentWithoutAnEdge')}</p>
              <button
                type="button"
                className={`${smallBtn} mt-2`}
                disabled={reloading}
                onClick={() => {
                  setReloading(true);
                  void hardReload();
                }}
              >
                {reloading ? t('routes.versions.versionsBuiltPage.reloading') : t('routes.versions.versionsBuiltPage.reloadTheLatestVersion')}
              </button>
            </div>
          )}
          {parity === 'unknown' && !refreshing && (
            <p className="mt-2 text-xs text-muted">
              <code className="font-mono">version.json</code>{' '}{t('routes.versions.versionsBuiltPage.isOnlyEmittedAtBuildTime')}</p>
          )}
        </Card>

        <Card title={t('routes.versions.versionsBuiltPage.frontEndBuild')}>
          <Row label={t('routes.versions.versionsBuiltPage.version')} value={META.version} mono />
          <Row label={t('routes.versions.versionsBuiltPage.buildDateTime')} value={fmtUTC(META.buildTime)} mono />
          <Row label={t('routes.versions.versionsBuiltPage.buildTimestampIso')} value={META.buildTime} mono />
          <Row label={t('routes.versions.versionsBuiltPage.gitCommit')} value={META.gitCommit} mono />
          <Row label={t('routes.versions.versionsBuiltPage.gitCommitFull')} value={META.gitCommitFull} mono />
          <Row label={t('routes.versions.versionsBuiltPage.gitBranch')} value={META.gitBranch} mono />
          <Row label={t('routes.versions.versionsBuiltPage.commitDate')} value={fmtUTC(META.gitCommitDate)} mono />
          <Row
            label={t('routes.versions.versionsBuiltPage.workingTree')}
            value={META.gitDirty ? 'dirty (uncommitted changes at build)' : 'clean'}
          />
        </Card>

        <Card title={t('routes.versions.versionsBuiltPage.runtimeConfigurationPerEnvironment')}>
          {cfgErr ? (
            <p className="text-sm text-danger">{t('routes.versions.versionsBuiltPage.configJsonErrorCfgerr', { cfgErr })}</p>
          ) : cfg ? (
            <>
              <Row label={t('routes.versions.versionsBuiltPage.environment')} value={env} />
              <Row label={t('routes.versions.versionsBuiltPage.apiBaseUrl')} value={cfg.apiBaseUrl ?? '—'} mono />
              <Row label={t('routes.versions.versionsBuiltPage.websocketUrl')} value={cfg.wsUrl ?? '—'} mono />
              <Row label={t('routes.versions.versionsBuiltPage.keycloakRealm')} value={realmOf(cfg.keycloak?.authority)} mono />
              <Row label={t('routes.versions.versionsBuiltPage.keycloakAuthority')} value={cfg.keycloak?.authority ?? '—'} mono />
              <Row label={t('routes.versions.versionsBuiltPage.keycloakClient')} value={cfg.keycloak?.clientId ?? '—'} mono />
            </>
          ) : (
            <p className="text-sm text-muted">{t('routes.versions.versionsBuiltPage.loadingConfig')}</p>
          )}
        </Card>

        <Card
          title={t('routes.versions.versionsBuiltPage.backEndApiHealth')}
          aside={
            health?.status ? (
              <Pill
                tone={
                  health.status === 'healthy'
                    ? 'success'
                    : health.status === 'degraded'
                      ? 'warning'
                      : 'danger'
                }
              >
                {health.status}
              </Pill>
            ) : undefined
          }
        >
          {healthErr ? (
            <p className="text-sm text-danger">{t('routes.versions.versionsBuiltPage.unreachableHealtherr', { healthErr })}</p>
          ) : health ? (
            <>
              <Row label={t('routes.versions.versionsBuiltPage.status')} value={health.status ?? '—'} />
              <Row label={t('routes.versions.versionsBuiltPage.apiVersion')} value={health.version ?? '—'} mono />
              <Row label={t('routes.versions.versionsBuiltPage.serverTime')} value={health.timestamp ? fmtUTC(health.timestamp) : '—'} mono />
              {typeof health.uptime_seconds === 'number' && (
                <Row label={t('routes.versions.versionsBuiltPage.uptime')} value={fmtUptime(health.uptime_seconds)} />
              )}
              {redis && (
                <Row
                  label={t('routes.versions.versionsBuiltPage.redisRateLimiting')}
                  value={
                    <span className="inline-flex items-center gap-2">
                      <span
                        className={`inline-block h-2 w-2 rounded-full ${statusTone(redis.status)}`}
                        aria-hidden="true"
                      />
                      {redis.status ?? '—'}
                      {typeof redis.latency_ms === 'number' && ` · ${redis.latency_ms} ms`}
                      {redis.error && ` · ${redis.error}`}
                    </span>
                  }
                />
              )}
            </>
          ) : (
            <p className="text-sm text-muted">{t('routes.versions.versionsBuiltPage.checkingBackend')}</p>
          )}
        </Card>

        <Card
          title={t('routes.versions.versionsBuiltPage.downstreamServicesApiHealthServices')}
          aside={
            services ? (
              <Pill
                tone={
                  services.healthy_count === services.total_count
                    ? 'success'
                    : services.healthy_count > 0
                      ? 'warning'
                      : 'danger'
                }
              >
                {services.healthy_count}{t('routes.versions.versionsBuiltPage.totalCountHealthy', { total_count: services.total_count })}</Pill>
            ) : undefined
          }
        >
          {servicesErr ? (
            <p className="text-sm text-danger">{t('routes.versions.versionsBuiltPage.unreachableServiceserr', { servicesErr })}</p>
          ) : services ? (
            services.services.length === 0 ? (
              <p className="text-sm text-muted">{t('routes.versions.versionsBuiltPage.noServiceDeclared')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
                      <th className="py-1.5 pr-3 font-medium">{t('routes.versions.versionsBuiltPage.service')}</th>
                      <th className="px-3 py-1.5 font-medium">{t('routes.versions.versionsBuiltPage.status2')}</th>
                      <th className="px-3 py-1.5 text-right font-medium">{t('routes.versions.versionsBuiltPage.latency')}</th>
                      <th className="py-1.5 pl-3 font-medium">{t('routes.versions.versionsBuiltPage.circuit')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...services.services]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((s) => (
                        <tr key={s.name} className="border-b border-border/60 align-top">
                          <td className="py-1.5 pr-3 font-mono text-xs">{s.name}</td>
                          <td className="px-3 py-1.5">
                            <span className="inline-flex items-center gap-2">
                              <span
                                className={`inline-block h-2 w-2 rounded-full ${statusTone(s.status)}`}
                                aria-hidden="true"
                              />
                              {s.status}
                            </span>
                            {s.error && (
                              <span className="block break-all text-xs text-danger">{s.error}</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums">
                            {typeof s.latency_ms === 'number' ? `${s.latency_ms} ms` : '—'}
                          </td>
                          <td className="py-1.5 pl-3 text-xs text-muted">
                            {s.circuit_state ?? '—'}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                <p className="mt-2 text-xs text-muted">{t('routes.versions.versionsBuiltPage.serverReadingV', { v: fmtUTC(services.timestamp) })}
                </p>
              </div>
            )
          ) : (
            <p className="text-sm text-muted">{t('routes.versions.versionsBuiltPage.checkingServices')}</p>
          )}
        </Card>

        <Card title={t('routes.versions.versionsBuiltPage.serviceWorkerOfflineCache')}>
          {sw === null ? (
            <p className="text-sm text-muted">{t('routes.versions.versionsBuiltPage.reading')}</p>
          ) : !sw.supported ? (
            <p className="text-sm text-muted">{t('routes.versions.versionsBuiltPage.notSupportedByThisBrowser')}</p>
          ) : (
            <>
              <Row label={t('routes.versions.versionsBuiltPage.saved')} value={sw.registered ? t('routes.versions.versionsBuiltPage.yes') : t('routes.versions.versionsBuiltPage.no')} />
              <Row label={t('routes.versions.versionsBuiltPage.active')} value={sw.active ? t('routes.versions.versionsBuiltPage.yes') : t('routes.versions.versionsBuiltPage.no')} />
              <Row
                label={t('routes.versions.versionsBuiltPage.updatePending')}
                value={
                  sw.waiting
                    ? t('routes.versions.versionsBuiltPage.yesWorkerWaiting')
                    : t('routes.versions.versionsBuiltPage.no')
                }
              />
              {sw.scope && <Row label={t('routes.versions.versionsBuiltPage.scope')} value={sw.scope} mono />}
            </>
          )}
        </Card>

        <footer className="pt-2 text-center text-xs text-muted">{t('routes.versions.versionsBuiltPage.checkedOnVRenderedOnV2', { v: fmtUTC(checkedAt), v2: fmtUTC(new Date().toISOString()) })}
        </footer>
      </div>
    </div>
  );
}

export default VersionsBuiltPage;
